import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  PRESCRIPTION_ADMINISTRATION_ROUTES,
  PRESCRIPTION_FREQUENCIES
} from '../utils/prescriptionOptions';

const PrescriptionDraftEditor = forwardRef(({
  title,
  description,
  drafts,
  onAdd,
  onChange,
  onRemove,
  invalidRows = [],
  disabled = false,
  t
}, ref) => {
  const sectionRef = useRef(null);
  const invalidFieldsByRow = useMemo(() => new Map(
    invalidRows.map((row) => [row.index, new Set(row.missingFields)])
  ), [invalidRows]);

  useImperativeHandle(ref, () => ({
    focusFirstInvalid: () => {
      sectionRef.current?.querySelector('[aria-invalid="true"]')?.focus();
    }
  }), []);

  const isInvalid = (index, field) => invalidFieldsByRow.get(index)?.has(field) || false;
  const fieldLabel = (key, index) => `${t(key)} ${index + 1}`;

  return (
    <section
      ref={sectionRef}
      className="prescription-editor"
      aria-labelledby="prescription-editor-title"
    >
      <div className="prescription-editor__heading">
        <h4 id="prescription-editor-title" className="report-subsection-title">{title}</h4>
        <p>{description}</p>
      </div>

      <div className="prescription-table-shell">
        <table className="prescription-table prescription-table--editable">
          <thead>
            <tr>
              <th scope="col">{t('reports.prescriptionDrugCode')}</th>
              <th scope="col">{t('reports.prescriptionDrugName')}</th>
              <th scope="col">{t('reports.prescriptionSpecification')}</th>
              <th scope="col">{t('reports.prescriptionAdministrationRoute')}</th>
              <th scope="col">{t('reports.prescriptionFrequency')}</th>
              <th scope="col">{t('reports.prescriptionSingleDose')}</th>
              <th
                scope="col"
                className="prescription-table__action-heading"
                aria-label={t('reports.prescriptionActions')}
              />
            </tr>
          </thead>
          <tbody>
            {drafts.map((draft, index) => (
              <tr className="prescription-table__draft-row" key={`prescription-draft-${index}`}>
                <td>
                  <input
                    type="text"
                    aria-label={fieldLabel('reports.prescriptionDrugCode', index)}
                    aria-invalid={isInvalid(index, 'drug_code')}
                    className={isInvalid(index, 'drug_code') ? 'prescription-field--invalid' : ''}
                    value={draft.drug_code}
                    onChange={(event) => onChange(index, 'drug_code', event.target.value)}
                    placeholder={t('reports.prescriptionDrugCodePlaceholder')}
                    disabled={disabled}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    aria-label={fieldLabel('reports.prescriptionDrugName', index)}
                    aria-invalid={isInvalid(index, 'drug_name')}
                    className={isInvalid(index, 'drug_name') ? 'prescription-field--invalid' : ''}
                    value={draft.drug_name}
                    onChange={(event) => onChange(index, 'drug_name', event.target.value)}
                    placeholder={t('reports.prescriptionDrugNamePlaceholder')}
                    disabled={disabled}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    aria-label={fieldLabel('reports.prescriptionSpecification', index)}
                    aria-invalid={isInvalid(index, 'specification')}
                    className={isInvalid(index, 'specification') ? 'prescription-field--invalid' : ''}
                    value={draft.specification}
                    onChange={(event) => onChange(index, 'specification', event.target.value)}
                    placeholder={t('reports.prescriptionSpecificationPlaceholder')}
                    disabled={disabled}
                  />
                </td>
                <td>
                  <select
                    aria-label={fieldLabel('reports.prescriptionAdministrationRoute', index)}
                    aria-invalid={isInvalid(index, 'administration_route')}
                    className={isInvalid(index, 'administration_route') ? 'prescription-field--invalid' : ''}
                    value={draft.administration_route}
                    onChange={(event) => onChange(index, 'administration_route', event.target.value)}
                    disabled={disabled}
                  >
                    <option value="">{t('reports.prescriptionSelectRoute')}</option>
                    {PRESCRIPTION_ADMINISTRATION_ROUTES.map((option) => (
                      <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    aria-label={fieldLabel('reports.prescriptionFrequency', index)}
                    aria-invalid={isInvalid(index, 'frequency')}
                    className={isInvalid(index, 'frequency') ? 'prescription-field--invalid' : ''}
                    value={draft.frequency}
                    onChange={(event) => onChange(index, 'frequency', event.target.value)}
                    disabled={disabled}
                  >
                    <option value="">{t('reports.prescriptionSelectFrequency')}</option>
                    {PRESCRIPTION_FREQUENCIES.map((option) => (
                      <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    aria-label={fieldLabel('reports.prescriptionSingleDose', index)}
                    aria-invalid={isInvalid(index, 'single_dose')}
                    className={isInvalid(index, 'single_dose') ? 'prescription-field--invalid' : ''}
                    value={draft.single_dose}
                    onChange={(event) => onChange(index, 'single_dose', event.target.value)}
                    placeholder={t('reports.prescriptionSingleDosePlaceholder')}
                    disabled={disabled}
                  />
                </td>
                <td className="prescription-table__action-cell">
                  <button
                    type="button"
                    className="prescription-editor__remove"
                    onClick={() => onRemove(index)}
                    disabled={disabled}
                    title={t('reports.removePrescriptionItem')}
                    aria-label={`${t('reports.removePrescriptionItem')} ${index + 1}`}
                  >
                    <Trash2 size={17} aria-hidden="true" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {invalidRows.length > 0 && (
        <p className="prescription-editor__error" role="alert">
          {t('reports.prescriptionIncompleteRowError')}
        </p>
      )}

      <div className="prescription-editor__footer">
        <button
          type="button"
          className="prescription-editor__add"
          onClick={onAdd}
          disabled={disabled}
        >
          <Plus size={17} aria-hidden="true" />
          <span>{t('reports.addPrescriptionItem')}</span>
        </button>
      </div>
    </section>
  );
});

PrescriptionDraftEditor.displayName = 'PrescriptionDraftEditor';

export default PrescriptionDraftEditor;
